import { defineMutator } from "@rocicorp/zero"

import type { ZeroJwtPayload } from "@productlane/lib/src/browser"

import type { MutatorTx } from "../schema"
import type {
	CreateSlackMessageArgs,
	DeleteSlackMessageArgs,
	ResendSlackMessageArgs,
} from "../types"
import type { JSONValue } from "@rocicorp/zero"

export const slackMessageMutators = {
	create: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: CreateSlackMessageArgs
			ctx: ZeroJwtPayload
		}) => {
			// Check if slack message already exists
			const [existingMessage] = await tx.query.slackMessages
				.where("id", "=", args.id)
				.run()

			if (existingMessage) {
				throw new Error("Slack message already exists")
			}

			if (tx.location !== "server" && args._text !== undefined) {
				args._text = "" // Will be filled by server-side from HTML
			}
			if (tx.location === "server" && args._text === undefined) {
				throw new Error("Text is required on server")
			}

			const now = Date.now()

			await tx.mutate.slackMessages.insert({
				id: args.id,
				createdAt: now,
				updatedAt: now,
				isDeleted: false,
				version: 1,
				workspaceId: authData.workspaceId,
				type: args.type,
				teamId: "",
				channelId: args.channelId,
				slackId: `temp_${now}`, // Temporary ID, will be updated after sending
				slackUserId: "",
				text: args._text ?? "", // Will be filled by server-side from HTML
				slackLink: "",
				html: args.html,
				threadId: args.threadId,
				userId: args.userId,
				attachments: args.attachments as unknown as JSONValue,
			})

			await tx.mutate.feedbacks.update({
				id: args.threadId,
				lastOutboundMessageAt: now,
			})
		},
	),
	resend: defineMutator(
		async ({ tx, args }: { tx: MutatorTx; args: ResendSlackMessageArgs }) => {
			const slackMessage = await tx.query.slackMessages
				.where("id", "=", args.id)
				.one()
				.run()

			if (!slackMessage) {
				throw new Error("Slack message not found")
			}
		},
	),
	delete: defineMutator(
		async ({ tx, args }: { tx: MutatorTx; args: DeleteSlackMessageArgs }) => {
			const slackMessage = await tx.query.slackMessages
				.where("id", "=", args.id)
				.one()
				.run()

			if (!slackMessage) {
				throw new Error("Slack message not found")
			}

			// Hard delete the slack message
			await tx.mutate.slackMessages.delete({ id: args.id })

			const lastOutboundMessage = await tx.query.slackMessages
				.where("threadId", "=", slackMessage.threadId)
				.where("type", "=", "OUTBOUND")
				.orderBy("createdAt", "desc")
				.one()
				.run()

			await tx.mutate.feedbacks.update({
				id: slackMessage.threadId,
				lastOutboundMessageAt: lastOutboundMessage?.createdAt ?? null,
				replied: true,
			})
		},
	),
}
